import { add, leftNormal, mul, norm, sub, type Pt } from "@/shared/lib/geometry";
import type { Cable, Fixture, WallGraph } from "./types";
import { pointAlongWall } from "./walls";

export const fixturePlanPoint = (graph: WallGraph, fixture: Fixture): Pt | null => {
	const wall = graph.walls[fixture.wallId];
	if (!wall) return null;
	const start = graph.nodes[wall.a];
	const end = graph.nodes[wall.b];
	if (!start || !end) return null;
	const along = norm(sub(end, start));
	const face = mul(leftNormal(along), (fixture.side * wall.thickness) / 2);
	return add(pointAlongWall(start, end, fixture.offset), face);
};

export const rerouteConnectedCables = (
	cables: Record<string, Cable>,
	movedId: string,
	previous: Pt,
	pointOf: (id: string) => Pt | null,
): Record<string, Cable> => {
	let updated = cables;
	for (const cable of Object.values(cables)) {
		const movedStart = cable.from === movedId;
		if (!movedStart && cable.to !== movedId) continue;
		const start = pointOf(cable.from);
		const end = pointOf(cable.to);
		if (!start || !end) continue;
		const differentFromPrevious = (point: Pt) => point.x !== previous.x || point.y !== previous.y;
		const neighbor = movedStart
			? (cable.points.find(differentFromPrevious) ?? end)
			: (cable.points.findLast(differentFromPrevious) ?? start);
		const horizontal = Math.abs(neighbor.x - previous.x) >= Math.abs(neighbor.y - previous.y);
		const corner = movedStart
			? horizontal
				? { x: end.x, y: start.y }
				: { x: start.x, y: end.y }
			: horizontal
				? { x: start.x, y: end.y }
				: { x: end.x, y: start.y };
		const points = start.x === end.x || start.y === end.y ? [] : [corner];
		if (updated === cables) updated = { ...cables };
		updated[cable.id] = { ...cable, points };
	}
	return updated;
};

export const bendCableSegment = (route: Pt[], segment: number, offset: number): Pt[] => {
	const start = route[segment];
	const end = route[segment + 1];
	if (!start || !end || offset === 0) return route.slice(1, -1);
	const horizontal = Math.abs(end.x - start.x) >= Math.abs(end.y - start.y);
	const shift = (point: Pt): Pt =>
		horizontal ? { x: point.x, y: point.y + offset } : { x: point.x + offset, y: point.y };
	const moved = [
		...route.slice(0, segment),
		...(segment === 0 ? [start] : []),
		shift(start),
		shift(end),
		...(segment === route.length - 2 ? [end] : []),
		...route.slice(segment + 2),
	];
	return moved.slice(1, -1);
};
